import type { QueryClient, QueryKey } from '@tanstack/react-query'
import type { QrReward, QrRewardAccepted } from './model'

export async function cancelQrRefreshes(
  client: QueryClient,
  scopeKey: QueryKey,
  selfKey?: QueryKey,
) {
  // A cancelled preflight must reject instead of resolving with old cached eligibility.
  await Promise.all([
    client.cancelQueries({ queryKey: scopeKey }, { revert: false }),
    ...(selfKey ? [client.cancelQueries({ queryKey: selfKey }, { revert: false })] : []),
  ])
}

type QrRewardRequest = {
  challengeId: string
  userId: string
  refresh: () => Promise<QrReward | undefined>
  submit: () => Promise<QrRewardAccepted>
  isCurrent: () => boolean
  accept: (preview: QrReward, accepted: QrRewardAccepted) => Promise<void>
  requireRecovery: () => void
}

export async function requestQrReward({
  challengeId,
  userId,
  refresh,
  submit,
  isCurrent,
  accept,
  requireRecovery,
}: QrRewardRequest) {
  // Read failures cannot have started wind-down, so only a submitted claim
  // needs the recovery lock while its server outcome is uncertain.
  const preview = await refresh()
  if (!isCurrent()) return
  if (preview?.state !== 'ELIGIBLE' || preview.blockers.length)
    throw new Error('Eligibility changed. Review your current challenge status.')
  try {
    const accepted = await submit()
    if (!isCurrent()) return
    if (
      accepted.status !== 'REWARD_PENDING_KYC' ||
      accepted.user_id !== userId ||
      accepted.trading_challenge_id !== challengeId
    )
      throw new Error('The response could not be confirmed.')
    await accept(preview, accepted)
    if (!isCurrent()) return
    await refresh()
  } catch (error) {
    if (isCurrent()) {
      requireRecovery()
      try {
        await refresh()
      } catch {
        /* Keep retry disabled until an explicit status refresh succeeds. */
      }
    }
    throw error
  }
}
